import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Film, Plus, Trash2, ExternalLink, Instagram, Youtube, Filter, Check, ChevronsUpDown, Heart, Camera, X, Share2 } from 'lucide-react';
import { copyToClipboard } from '../lib/clipboard';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import InitialsAvatar from '../components/InitialsAvatar';

const PLATFORMS = [
  { id: 'instagram', label: 'IG' },
  { id: 'tiktok', label: 'TT' },
  { id: 'youtube', label: 'YT' },
  { id: 'other', label: 'other' },
];

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M19.6 6.8a5.6 5.6 0 0 1-3.3-1.1V15a5.4 5.4 0 1 1-5.4-5.4c.3 0 .6 0 .9.1v2.6a2.8 2.8 0 1 0 2 2.7V2h2.5a3.4 3.4 0 0 0 3.3 3v1.8Z"/>
    </svg>
  );
}
const PLATFORM_ICON = { instagram: Instagram, tiktok: TikTokIcon, youtube: Youtube, other: Film };

const nfmt = (v) => (v == null || v === '' ? '' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 }));
const todayISO = () => new Date().toISOString().slice(0, 10);

// -----------------------------------------------------------------------------
const ContentData = () => {
  const { talent, brands, brandMap, talentMap } = useDeals();
  const { user } = useAuth();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [talentFilter, setTalentFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');   // 'all' | 'paid' | 'organic'
  const [addingFor, setAddingFor] = useState(null);   // talent object when dialog is open

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/content-data');
        setContent(data || []);
      } catch { toast.error('failed to load content'); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => content.filter((c) => {
    if (talentFilter !== 'all' && c.talent_id !== talentFilter) return false;
    if (brandFilter !== 'all' && !(c.brand_ids || (c.brand_id ? [c.brand_id] : [])).includes(brandFilter)) return false;
    if (typeFilter !== 'all' && c.paid_or_organic !== typeFilter) return false;
    return true;
  }), [content, talentFilter, brandFilter, typeFilter]);

  const grouped = useMemo(() => {
    const byTalent = new Map();
    const visibleTalent = isManager ? talent : talent.filter((t) => t.user_id === user?.user_id);
    visibleTalent.forEach((t) => byTalent.set(t.user_id, { talent: t, items: [] }));
    filtered.forEach((c) => {
      if (!byTalent.has(c.talent_id)) byTalent.set(c.talent_id, { talent: talent.find((t) => t.user_id === c.talent_id), items: [] });
      byTalent.get(c.talent_id).items.push(c);
    });
    return Array.from(byTalent.values())
      .filter((g) => talentFilter === 'all' || g.talent?.user_id === talentFilter)
      .sort((a, b) => (a.talent?.name || '').localeCompare(b.talent?.name || ''));
  }, [filtered, talent, talentFilter, isManager, user]);

  const createPiece = async (payload) => {
    try {
      const { data } = await api.post('/content-data', payload);
      setContent((prev) => [data, ...prev]);
      setAddingFor(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'add failed'); }
  };

  const patch = async (id, updates) => {
    try {
      const { data } = await api.patch(`/content-data/${id}`, updates);
      setContent((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));
    } catch { toast.error('save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('remove this content piece?')) return;
    try {
      await api.delete(`/content-data/${id}`);
      setContent((prev) => prev.filter((c) => c.id !== id));
    } catch { toast.error('delete failed'); }
  };

  return (
    <div className="p-6 lg:p-8">
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
          <Film className="w-6 h-6 text-pink-500" /> content data
        </h1>
        <p className="text-sm text-slate-500 mt-1">every piece of published content per talent. tag a brand to feed a case study later.</p>
      </header>

      {/* filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-4 flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-slate-400" />
        {isManager && (
          <Select value={talentFilter} onValueChange={setTalentFilter}>
            <SelectTrigger className="h-9 w-[180px] text-xs" data-testid="content-talent-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all talent</SelectItem>
              {talent.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <BrandCombobox
          value={brandFilter}
          onChange={setBrandFilter}
          brands={brands}
          includeAll
          placeholder="all brands"
          width="w-[200px]"
          testId="content-brand-filter"
        />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-9 w-[140px] text-xs" data-testid="content-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">all types</SelectItem>
            <SelectItem value="paid">💸 paid</SelectItem>
            <SelectItem value="organic">🌱 organic</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-[11px] text-slate-500">{filtered.length} piece{filtered.length === 1 ? '' : 's'}</div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">loading…</div>
      ) : grouped.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-sm text-slate-400">no talent yet.</div>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <TalentBlock
              key={g.talent?.user_id || '__none__'}
              talent={g.talent}
              items={g.items}
              brands={brands}
              brandMap={brandMap}
              canEdit={isManager || g.talent?.user_id === user?.user_id}
              onAdd={() => setAddingFor(g.talent)}
              onPatch={patch}
              onRemove={remove}
            />
          ))}
        </div>
      )}

      {addingFor && (
        <AddContentDialog
          open={Boolean(addingFor)}
          talent={addingFor}
          brands={brands}
          onClose={() => setAddingFor(null)}
          onSave={createPiece}
        />
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
const TalentBlock = ({ talent, items, brands, brandMap, canEdit, onAdd, onPatch, onRemove }) => {
  const [brandFilter, setBrandFilter] = useState('all');   // brand id, 'all', '__untagged__', or '__favs__'
  const [sharing, setSharing] = useState(false);
  const isRealBrandFilter = talent && !['all', '__favs__', '__untagged__'].includes(brandFilter);

  const shareBrand = async () => {
    if (!isRealBrandFilter) return;
    setSharing(true);
    try {
      const { data } = await api.post('/content-data/share', { talent_id: talent.user_id, brand_id: brandFilter });
      const url = `${window.location.origin}/share/content/${data.token}`;
      const copied = await copyToClipboard(url);
      if (copied) toast.success('view-only link copied — no login needed');
      else toast.success('view-only link ready', { description: url, duration: 15000 });
    } catch { toast.error('failed to create share link'); }
    finally { setSharing(false); }
  };

  // Brands this talent has posted content for, with counts
  const brandBubbles = useMemo(() => {
    const counts = {};
    items.forEach((c) => {
      const ids = c.brand_ids && c.brand_ids.length ? c.brand_ids : (c.brand_id ? [c.brand_id] : ['__untagged__']);
      ids.forEach((key) => { counts[key] = (counts[key] || 0) + 1; });
    });
    return Object.entries(counts)
      .map(([id, count]) => ({
        id,
        count,
        brand: id === '__untagged__' ? null : brandMap[id],
      }))
      .sort((a, b) => b.count - a.count);
  }, [items, brandMap]);

  const favCount = items.filter((c) => c.favorited).length;

  const visibleItems = brandFilter === 'all'
    ? items
    : brandFilter === '__favs__'
      ? items.filter((c) => c.favorited)
      : items.filter((c) => (c.brand_ids && c.brand_ids.length ? c.brand_ids : (c.brand_id ? [c.brand_id] : ['__untagged__'])).includes(brandFilter));

  const totals = visibleItems.reduce((acc, c) => {
    acc.views += Number(c.views) || 0;
    acc.likes += Number(c.likes) || 0;
    acc.comments += Number(c.comments) || 0;
    acc.saves += Number(c.saves) || 0;
    acc.shares += Number(c.shares) || 0;
    return acc;
  }, { views: 0, likes: 0, comments: 0, saves: 0, shares: 0 });

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-pink-200" style={{ background: '#d394ab' }}>
        {talent ? <InitialsAvatar name={talent.name} size={32} /> : <div className="w-8 h-8 rounded-full bg-white/30 border border-dashed border-white/50" />}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-white truncate">{talent?.name || 'unassigned'}</div>
          <div className="text-xs text-white/80">{items.length} piece{items.length === 1 ? '' : 's'}</div>
        </div>
        <div className="hidden md:flex items-center gap-3 text-[10px] text-white/90 uppercase tracking-wide">
          <TotalPill label="views" v={totals.views} />
          <TotalPill label="likes" v={totals.likes} />
          <TotalPill label="comments" v={totals.comments} />
          <TotalPill label="saves" v={totals.saves} />
          <TotalPill label="shares" v={totals.shares} />
        </div>
        {canEdit && talent && (
          <Button onClick={onAdd} className="h-8 bg-white text-pink-700 hover:bg-pink-50 text-xs shrink-0" data-testid={`content-add-${talent.user_id}`}>
            <Plus className="w-3 h-3 mr-1" /> add
          </Button>
        )}
      </div>

      {/* Brand + favourites bubbles — click one to filter this talent's content */}
      {(brandBubbles.length > 0 || favCount > 0) && (
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-1.5 bg-pink-50/30">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1">filter</span>
          <BrandBubble
            active={brandFilter === 'all'}
            onClick={() => setBrandFilter('all')}
            emoji=""
            name="all"
            count={items.length}
          />
          {favCount > 0 && (
            <button
              type="button"
              onClick={() => setBrandFilter('__favs__')}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium border transition ${brandFilter === '__favs__'
                ? 'bg-pink-500 text-white border-pink-500 shadow-sm'
                : 'bg-white text-pink-600 border-pink-200 hover:border-pink-400'}`}
              data-testid="brand-bubble-favs"
            >
              <Heart className="w-3 h-3" fill="currentColor" />
              <span>favourites</span>
              <span className={`tabular-nums text-[10px] font-semibold ${brandFilter === '__favs__' ? 'text-white/80' : 'text-pink-400'}`}>{favCount}</span>
            </button>
          )}
          {brandBubbles.map((b) => (
            <BrandBubble
              key={b.id}
              active={brandFilter === b.id}
              onClick={() => setBrandFilter(b.id)}
              emoji={b.brand?.logo}
              name={b.brand?.name || 'untagged'}
              count={b.count}
            />
          ))}
          {isRealBrandFilter && (
            <button
              type="button"
              onClick={shareBrand}
              disabled={sharing}
              className="ml-auto inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-semibold border border-pink-200 bg-white text-pink-600 hover:border-pink-400 hover:bg-pink-50 transition disabled:opacity-50"
              title="copy a view-only link to this brand's content dashboard"
              data-testid={`content-share-btn-${talent?.user_id}`}
            >
              <Share2 className="w-3 h-3" /> {sharing ? 'creating…' : 'share'}
            </button>
          )}
        </div>
      )}

      {visibleItems.length === 0 ? (
        <div className="px-4 py-8 text-center text-xs text-slate-400 italic">
          {items.length === 0 ? 'no content logged yet' : 'no content for this brand yet'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" style={{ minWidth: '1200px' }}>
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th style={{ width: '32px' }}></th>
                <th className="text-left font-medium px-3 py-2" style={{ width: '90px' }}>date</th>
                <th className="text-left font-medium px-3 py-2" style={{ width: '80px' }}>platform</th>
                <th className="text-left font-medium px-3 py-2" style={{ width: '110px' }}>type</th>
                <th className="text-left font-medium px-3 py-2" style={{ width: '190px' }}>brand</th>
                <th className="text-left font-medium px-3 py-2">link</th>
                <th className="text-right font-medium px-2 py-2" style={{ width: '85px' }}>views</th>
                <th className="text-right font-medium px-2 py-2" style={{ width: '75px' }}>likes</th>
                <th className="text-right font-medium px-2 py-2" style={{ width: '85px' }}>comments</th>
                <th className="text-right font-medium px-2 py-2" style={{ width: '70px' }}>saves</th>
                <th className="text-right font-medium px-2 py-2" style={{ width: '75px' }}>shares</th>
                <th style={{ width: '30px' }}></th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((c) => (
                <ContentRow
                  key={c.id}
                  item={c}
                  brands={brands}
                  brandMap={brandMap}
                  canEdit={canEdit}
                  onPatch={onPatch}
                  onRemove={onRemove}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const BrandBubble = ({ active, onClick, emoji, name, count }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium border transition ${active
      ? 'bg-pink-500 text-white border-pink-500 shadow-sm'
      : 'bg-white text-slate-700 border-slate-200 hover:border-pink-300 hover:text-pink-600'}`}
    data-testid={`brand-bubble-${name}`}
  >
    {emoji && <span className="text-[13px] leading-none">{emoji}</span>}
    <span className="truncate max-w-[130px]">{name}</span>
    <span className={`tabular-nums text-[10px] font-semibold ${active ? 'text-white/80' : 'text-slate-400'}`}>{count}</span>
  </button>
);

const TotalPill = ({ label, v }) => (
  <div className="flex flex-col items-end leading-tight">
    <span className="text-[9px] opacity-80">{label}</span>
    <span className="text-[12px] font-semibold tabular-nums">{v ? nfmt(v) : '—'}</span>
  </div>
);

// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// Manual screenshot upload — clickable thumbnail that overrides the auto link
// preview once set. Falls back to the fetched link preview, then a platform icon.
// -----------------------------------------------------------------------------
const uploadScreenshot = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/uploads/content-screenshot', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data.url;
};

const ScreenshotThumb = ({ screenshotUrl, previewImage, PlatformIcon, canEdit, onUploaded, onCleared, testId }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const img = screenshotUrl || previewImage;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadScreenshot(file);
      await onUploaded(url);
    } catch (err) { toast.error(err?.response?.data?.detail || 'screenshot upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className="relative w-10 h-10 shrink-0 group/thumb">
      {img ? (
        <img src={img} alt="" className="w-10 h-10 rounded-md object-cover border border-slate-200" loading="lazy" />
      ) : (
        <div className="w-10 h-10 rounded-md bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-300">
          <PlatformIcon className="w-4 h-4" />
        </div>
      )}
      {canEdit && (
        <>
          <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={testId} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            title={screenshotUrl ? 'replace screenshot' : 'upload a screenshot'}
            className="absolute inset-0 rounded-md bg-black/0 group-hover/thumb:bg-black/45 text-white opacity-0 group-hover/thumb:opacity-100 flex items-center justify-center transition"
            data-testid={`${testId}-trigger`}
          >
            {uploading ? <span className="text-[9px]">…</span> : <Camera className="w-3.5 h-3.5" />}
          </button>
          {screenshotUrl && (
            <button
              type="button"
              onClick={onCleared}
              title="remove screenshot"
              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-700 text-white flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition"
              data-testid={`${testId}-clear`}
            ><X className="w-2.5 h-2.5" /></button>
          )}
        </>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
const ContentRow = ({ item, brands, brandMap, canEdit, onPatch, onRemove }) => {
  const brandIds = item.brand_ids && item.brand_ids.length ? item.brand_ids : (item.brand_id ? [item.brand_id] : []);
  const PlatformIcon = PLATFORM_ICON[item.platform] || Film;
  const href = item.link && /^https?:\/\//i.test(item.link) ? item.link : (item.link ? `https://${item.link}` : '');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!item.link || item.link.trim() === '') { setPreview(null); return; }
    api.get('/link-preview', { params: { url: item.link } })
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [item.link]);

  return (
    <tr className="border-t border-slate-100 hover:bg-pink-50/30 group">
      <td className="px-2 py-1.5 text-center">
        <button
          type="button"
          onClick={() => onPatch(item.id, { favorited: !item.favorited })}
          disabled={!canEdit}
          className={`transition ${item.favorited ? 'text-pink-500 hover:text-pink-600' : 'text-slate-300 hover:text-pink-400'}`}
          title={item.favorited ? 'unfavourite' : 'favourite'}
          data-testid={`fav-${item.id}`}
        >
          <Heart className="w-4 h-4" fill={item.favorited ? 'currentColor' : 'none'} />
        </button>
      </td>
      <td className="px-3 py-1.5">
        <input
          type="date"
          value={item.posted_at || ''}
          onChange={(e) => onPatch(item.id, { posted_at: e.target.value })}
          disabled={!canEdit}
          className="h-7 text-xs bg-transparent outline-none w-full"
        />
      </td>
      <td className="px-3 py-1.5">
        <Select value={item.platform || 'instagram'} onValueChange={(v) => onPatch(item.id, { platform: v })} disabled={!canEdit}>
          <SelectTrigger className="h-7 text-xs w-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PLATFORMS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-1.5">
        <PaidOrganicPill
          value={item.paid_or_organic}
          onChange={(v) => onPatch(item.id, { paid_or_organic: v })}
          disabled={!canEdit}
          testIdPrefix={`content-type-${item.id}`}
        />
      </td>
      <td className="px-3 py-1.5">
        <MultiBrandCombobox
          value={brandIds}
          onChange={(ids) => onPatch(item.id, { brand_ids: ids })}
          brands={brands}
          width="w-full"
          size="sm"
          disabled={!canEdit}
          testId={`content-brand-${item.id}`}
        />
      </td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-2">
          <ScreenshotThumb
            screenshotUrl={item.screenshot_url}
            previewImage={preview?.image}
            PlatformIcon={PlatformIcon}
            canEdit={canEdit}
            onUploaded={(url) => onPatch(item.id, { screenshot_url: url })}
            onCleared={() => onPatch(item.id, { screenshot_url: '' })}
            testId={`content-screenshot-${item.id}`}
          />
          <Input
            value={item.link || ''}
            onChange={(e) => onPatch(item.id, { link: e.target.value })}
            placeholder="url"
            disabled={!canEdit}
            className="h-7 text-xs"
            data-emoji-skip="true"
          />
          {href && (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-pink-600 shrink-0" title="open">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </td>
      <MetricCell value={item.views}    canEdit={canEdit} onCommit={(v) => onPatch(item.id, { views: v })} />
      <MetricCell value={item.likes}    canEdit={canEdit} onCommit={(v) => onPatch(item.id, { likes: v })} />
      <MetricCell value={item.comments} canEdit={canEdit} onCommit={(v) => onPatch(item.id, { comments: v })} />
      <MetricCell value={item.saves}    canEdit={canEdit} onCommit={(v) => onPatch(item.id, { saves: v })} />
      <MetricCell value={item.shares}   canEdit={canEdit} onCommit={(v) => onPatch(item.id, { shares: v })} />
      <td className="px-2 py-1.5 text-right">
        {canEdit && (
          <button onClick={() => onRemove(item.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600" title="remove">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
};

const MetricCell = ({ value, canEdit, onCommit }) => {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  useEffect(() => { setDraft(value == null ? '' : String(value)); }, [value]);
  return (
    <td className="px-2 py-1.5 text-right">
      <input
        type="number"
        step="any"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { const next = draft === '' ? null : Number(draft); if (next !== value) onCommit(next); }}
        disabled={!canEdit}
        placeholder="—"
        className="h-7 w-full text-right tabular-nums text-xs bg-transparent outline-none placeholder:text-slate-300"
      />
    </td>
  );
};

// -----------------------------------------------------------------------------
// Searchable brand picker (popover + cmdk). Used for filters and per-row tagging.
// -----------------------------------------------------------------------------
const BrandCombobox = ({ value, onChange, brands, includeAll = false, allLabel = 'all brands', placeholder = 'select brand', width = 'w-[220px]', size = 'md', disabled, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = value && value !== 'all' && value !== '__untagged__'
    ? brands.find((b) => b.id === value)
    : null;
  const untagged = value === '__untagged__';
  const btnClass = `${size === 'sm' ? 'h-7 text-xs' : 'h-9 text-xs'} justify-between ${width} font-normal`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button variant="outline" role="combobox" className={btnClass} data-testid={testId}>
          <span className="truncate flex items-center gap-1.5">
            {selected ? (
              <>
                {selected.logo && <span>{selected.logo}</span>}
                <span className="truncate">{selected.name}</span>
              </>
            ) : untagged ? (
              <span className="text-slate-500">— untagged —</span>
            ) : value === 'all' ? (
              <span className="text-slate-500">{allLabel}</span>
            ) : (
              <span className="text-slate-400">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="search brand…" className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty>no brand found</CommandEmpty>
            <CommandGroup>
              {includeAll && (
                <CommandItem value="__all__" onSelect={() => { onChange('all'); setOpen(false); }}>
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                  {allLabel}
                </CommandItem>
              )}
              <CommandItem value="__untagged__" onSelect={() => { onChange('__untagged__'); setOpen(false); }}>
                <Check className={`mr-2 h-3.5 w-3.5 ${value === '__untagged__' ? 'opacity-100' : 'opacity-0'}`} />
                <span className="text-slate-500">— untagged —</span>
              </CommandItem>
              {brands.map((b) => (
                <CommandItem
                  key={b.id}
                  value={`${b.name} ${b.logo || ''}`}
                  onSelect={() => { onChange(b.id); setOpen(false); }}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === b.id ? 'opacity-100' : 'opacity-0'}`} />
                  {b.logo && <span className="mr-1.5">{b.logo}</span>}
                  <span className="truncate">{b.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// -----------------------------------------------------------------------------
// Multi-brand picker (popover + cmdk, checkbox-style). Used for tagging a content
// piece with several brands at once (e.g. a multi-brand collab post).
// -----------------------------------------------------------------------------
const MultiBrandCombobox = ({ value = [], onChange, brands, width = 'w-[220px]', size = 'md', disabled, testId, placeholder = 'tag brand(s)' }) => {
  const [open, setOpen] = useState(false);
  const selected = value.map((id) => brands.find((b) => b.id === id)).filter(Boolean);
  const btnClass = `${size === 'sm' ? 'h-7 text-xs' : 'h-9 text-xs'} justify-between ${width} font-normal`;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button variant="outline" role="combobox" className={btnClass} data-testid={testId}>
          {selected.length === 0 ? (
            <span className="text-slate-400 truncate">{placeholder}</span>
          ) : (
            <span className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden">
              {selected.slice(0, 2).map((b) => (
                <span key={b.id} className="inline-flex items-center gap-0.5 bg-pink-50 text-pink-700 rounded px-1.5 py-0.5 text-[10px] shrink-0 max-w-[70px]">
                  {b.logo && <span>{b.logo}</span>}<span className="truncate">{b.name}</span>
                </span>
              ))}
              {selected.length > 2 && <span className="text-[10px] text-slate-400 shrink-0">+{selected.length - 2}</span>}
            </span>
          )}
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="search brand…" className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty>no brand found</CommandEmpty>
            <CommandGroup>
              {brands.map((b) => (
                <CommandItem
                  key={b.id}
                  value={`${b.name} ${b.logo || ''}`}
                  onSelect={() => toggle(b.id)}
                  data-testid={`${testId}-option-${b.id}`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${value.includes(b.id) ? 'opacity-100' : 'opacity-0'}`} />
                  {b.logo && <span className="mr-1.5">{b.logo}</span>}
                  <span className="truncate">{b.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// -----------------------------------------------------------------------------
// Full-fat "add content" dialog — every field including metrics up-front.
// -----------------------------------------------------------------------------
const AddContentDialog = ({ open, talent, brands, onClose, onSave }) => {
  const [form, setForm] = useState({
    posted_at: todayISO(),
    platform: 'instagram',
    brand_ids: [],
    paid_or_organic: null,
    link: '',
    screenshot_url: '',
    views: '', likes: '', comments: '', saves: '', shares: '',
    notes: '',
    favorited: false,
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadScreenshot(file);
      set({ screenshot_url: url });
    } catch (err) { toast.error(err?.response?.data?.detail || 'screenshot upload failed'); }
    finally { setUploading(false); }
  };

  const submit = () => {
    onSave({
      talent_id: talent.user_id,
      posted_at: form.posted_at || null,
      platform: form.platform,
      brand_ids: form.brand_ids,
      paid_or_organic: form.paid_or_organic,
      favorited: form.favorited,
      link: form.link.trim(),
      screenshot_url: form.screenshot_url || null,
      views: form.views === '' ? null : Number(form.views),
      likes: form.likes === '' ? null : Number(form.likes),
      comments: form.comments === '' ? null : Number(form.comments),
      saves: form.saves === '' ? null : Number(form.saves),
      shares: form.shares === '' ? null : Number(form.shares),
      notes: form.notes,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>add content for {talent?.name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">date</label>
            <Input type="date" value={form.posted_at} onChange={(e) => set({ posted_at: e.target.value })} className="mt-1 h-9 text-xs" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">platform</label>
            <Select value={form.platform} onValueChange={(v) => set({ platform: v })}>
              <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500">brand(s)</label>
            <div className="mt-1">
              <MultiBrandCombobox
                value={form.brand_ids}
                onChange={(ids) => set({ brand_ids: ids })}
                brands={brands}
                width="w-full"
                placeholder="tag one or more brands…"
                testId="add-content-brand"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">type</label>
            <div className="mt-1"><PaidOrganicPill value={form.paid_or_organic} onChange={(v) => set({ paid_or_organic: v })} testIdPrefix="add-content-type" /></div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">favourite</label>
            <button
              type="button"
              onClick={() => set({ favorited: !form.favorited })}
              className={`mt-1 h-9 w-full rounded-md border text-xs font-medium inline-flex items-center justify-center gap-1.5 transition ${form.favorited ? 'bg-pink-50 border-pink-300 text-pink-700' : 'bg-white border-slate-200 text-slate-500 hover:border-pink-200'}`}
              data-testid="add-content-fav"
            >
              <Heart className="w-3.5 h-3.5" fill={form.favorited ? 'currentColor' : 'none'} />
              {form.favorited ? 'pinned' : 'pin as favourite'}
            </button>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500">link</label>
            <Input value={form.link} onChange={(e) => set({ link: e.target.value })} placeholder="https://…" className="mt-1 h-9 text-xs" data-testid="add-content-link" data-emoji-skip="true" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500">preview screenshot (optional)</label>
            <div className="mt-1 flex items-center gap-2">
              <div className="relative w-10 h-10 shrink-0">
                {form.screenshot_url ? (
                  <img src={form.screenshot_url} alt="" className="w-10 h-10 rounded-md object-cover border border-slate-200" />
                ) : (
                  <div className="w-10 h-10 rounded-md bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-slate-300">
                    <Camera className="w-4 h-4" />
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="add-content-screenshot" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-9 text-xs" data-testid="add-content-screenshot-trigger">
                {uploading ? 'uploading…' : form.screenshot_url ? 'replace' : 'upload screenshot'}
              </Button>
              {form.screenshot_url && (
                <button type="button" onClick={() => set({ screenshot_url: '' })} className="text-slate-400 hover:text-red-600" title="remove" data-testid="add-content-screenshot-clear">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="col-span-2 grid grid-cols-5 gap-2">
            {['views', 'likes', 'comments', 'saves', 'shares'].map((k) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-widest text-slate-500">{k}</label>
                <Input type="number" step="any" value={form[k]} onChange={(e) => set({ [k]: e.target.value })} placeholder="—" className="mt-1 h-9 text-xs" data-testid={`add-content-${k}`} />
              </div>
            ))}
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500">notes (optional)</label>
            <Input value={form.notes} onChange={(e) => set({ notes: e.target.value })} className="mt-1 h-9 text-xs" placeholder="anything worth remembering…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>cancel</Button>
          <Button onClick={submit} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="add-content-save">save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContentData;

// -----------------------------------------------------------------------------
// Paid / Organic tri-state pill.
// -----------------------------------------------------------------------------
const PO_STYLES = {
  paid:    { bg: '#fce7f3', ink: '#9d174d', border: '#f9a8c8' },   // pink
  organic: { bg: '#dcfce7', ink: '#166534', border: '#86efac' },   // emerald
};
const PaidOrganicPill = ({ value, onChange, disabled, testIdPrefix = 'po' }) => {
  const cycle = () => {
    if (disabled) return;
    onChange(value === 'paid' ? 'organic' : value === 'organic' ? null : 'paid');
  };
  if (!value) {
    return (
      <button
        type="button"
        onClick={cycle}
        disabled={disabled}
        className="inline-flex items-center h-7 px-2.5 rounded-full text-[11px] font-medium border border-dashed border-slate-200 text-slate-400 hover:border-pink-300 hover:text-pink-600"
        data-testid={`${testIdPrefix}-none`}
      >
        tag type
      </button>
    );
  }
  const st = PO_STYLES[value];
  return (
    <button
      type="button"
      onClick={cycle}
      disabled={disabled}
      className="inline-flex items-center h-7 px-2.5 rounded-full text-[11px] font-semibold border"
      style={{ background: st.bg, color: st.ink, borderColor: st.border }}
      title={disabled ? undefined : 'tap to cycle: paid → organic → none'}
      data-testid={`${testIdPrefix}-${value}`}
    >
      {value === 'paid' ? '💸 paid' : '🌱 organic'}
    </button>
  );
};

// -----------------------------------------------------------------------------
// Favourites strip — quick view of every hearted piece across all talent.
// -----------------------------------------------------------------------------
const FavoritesStrip = ({ items, talentMap, brandMap, onToggle }) => {
  if (!items.length) return null;
  return (
    <div className="bg-white border border-pink-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-4 py-2.5 border-b border-pink-100 flex items-center gap-2 bg-pink-50/60">
        <Heart className="w-4 h-4 text-pink-500" fill="currentColor" />
        <div className="text-sm font-semibold text-pink-700">favourites</div>
        <div className="text-[11px] text-pink-500 ml-1">{items.length}</div>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((c) => {
          const t = talentMap[c.talent_id];
          const b = c.brand_id ? brandMap[c.brand_id] : null;
          const href = c.link && /^https?:\/\//i.test(c.link) ? c.link : (c.link ? `https://${c.link}` : '');
          return (
            <li key={c.id} className="px-4 py-2 flex items-center gap-3 hover:bg-pink-50/30 group">
              <button
                type="button"
                onClick={() => onToggle(c.id, c.favorited)}
                className="text-pink-500 hover:text-pink-600 shrink-0"
                title="unfavourite"
              >
                <Heart className="w-4 h-4" fill="currentColor" />
              </button>
              <span className="text-[11px] text-slate-500 tabular-nums w-24 shrink-0">{c.posted_at || '—'}</span>
              <span className="text-[11px] font-semibold text-slate-700 w-32 truncate shrink-0">{t?.name || 'unknown'}</span>
              {b && (
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 shrink-0 max-w-[140px]">
                  {b.logo && <span>{b.logo}</span>}
                  <span className="truncate">{b.name}</span>
                </span>
              )}
              {c.paid_or_organic && <PaidOrganicPill value={c.paid_or_organic} onChange={() => {}} disabled testIdPrefix={`content-type-fav-${c.id}`} />}
              <a
                href={href || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { if (!href) e.preventDefault(); }}
                className={`flex-1 min-w-0 truncate text-[12px] ${href ? 'text-pink-700 hover:underline' : 'text-slate-400 italic'}`}
              >{href || 'no link'}</a>
              <span className="text-[11px] tabular-nums text-slate-500 shrink-0">
                {c.views != null && <>{nfmt(c.views)} views · </>}
                {c.likes != null && <>{nfmt(c.likes)} ♥</>}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
